import {
  bubbleDayOfEmergencyGetCurrentQuery,
  bubbleEmergencyDeclarationsGetActiveQuery,
  bubbleEmergencyDeclarationsListActiveQuery,
  bubbleEmergencyDeclarationsListQuery,
  useBubbleEmergencyDeclarationsEnd,
  useBubbleEmergencyDeclarationsGetActive,
  useBubbleEmergencyDispatchDispatch,
} from "@crowdcoursing/api/openapi";
import { Alert, Snackbar } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { QuickResponseTemplate } from "~ui/templates/QuickResponseTemplate/QuickResponseTemplate";

import {
  type DispatchEmergencyKey,
  isDispatchKey,
} from "../../templates/AdminTemplate/BubbleGroups/emergencyQuickResponseActions";

export interface QuickResponseModalProps {
  /** School bubble id (URL segment under `/school/:schoolId/visitor-management`). */
  bubbleId: string;
  onClose: () => void;
  open: boolean;
}

function formatApiError(err: unknown): string {
  if (err && typeof err === "object") {
    if ("payload" in err && typeof (err as { payload?: unknown }).payload === "string") {
      return (err as { payload: string }).payload;
    }
    if ("message" in err && typeof (err as { message?: unknown }).message === "string") {
      return (err as { message: string }).message;
    }
  }
  return "Request failed.";
}

/**
 * Emergency / SRP action picker for visitor management.
 * Dispatches map to `emergency-dispatch`; false alarm ends the active declaration; custom is not a server key yet.
 */
export function QuickResponseModal({ bubbleId, onClose, open }: QuickResponseModalProps) {
  const queryClient = useQueryClient();
  const [snackbar, setSnackbar] = useState<{ message: string; severity: "error" | "info" | "success" } | null>(null);

  const invalidateDeclarationQueries = useCallback(() => {
    if (!bubbleId) {
      return;
    }
    void queryClient.invalidateQueries({
      queryKey: bubbleEmergencyDeclarationsGetActiveQuery({ pathParams: { bubbleId } }).queryKey,
    });
    void queryClient.invalidateQueries({
      queryKey: bubbleEmergencyDeclarationsListQuery({ pathParams: { bubbleId } }).queryKey,
    });
    void queryClient.invalidateQueries({
      queryKey: bubbleEmergencyDeclarationsListActiveQuery({ pathParams: { bubbleId } }).queryKey,
    });
    void queryClient.invalidateQueries({
      queryKey: bubbleDayOfEmergencyGetCurrentQuery({ pathParams: { bubbleId } }).queryKey,
    });
    void queryClient.invalidateQueries({
      queryKey: ["api", "v1", "Bubbles", bubbleId, "day-of-emergency"],
    });
  }, [bubbleId, queryClient]);

  const dispatchMutation = useBubbleEmergencyDispatchDispatch({
    onError: (err) => {
      setSnackbar({ message: formatApiError(err), severity: "error" });
    },
    onSuccess: () => {
      invalidateDeclarationQueries();
      setSnackbar({ message: "Emergency dispatched.", severity: "success" });
      onClose();
    },
  });

  const endDeclarationMutation = useBubbleEmergencyDeclarationsEnd({
    onError: (err) => {
      setSnackbar({ message: formatApiError(err), severity: "error" });
    },
    onSuccess: () => {
      invalidateDeclarationQueries();
      setSnackbar({ message: "Emergency ended as false alarm.", severity: "success" });
      onClose();
    },
  });

  const activeDeclarationQuery = useBubbleEmergencyDeclarationsGetActive(
    { pathParams: { bubbleId } },
    { enabled: !!bubbleId && open, retry: false },
  );

  const [falseAlarmLoading, setFalseAlarmLoading] = useState(false);

  const isBusy = dispatchMutation.isPending || endDeclarationMutation.isPending;

  const dispatchEmergency = useCallback(
    (emergencyKey: DispatchEmergencyKey) => {
      if (!bubbleId || isBusy) {
        return;
      }
      void dispatchMutation.mutateAsync({
        body: { emergencyKey, isDrill: false },
        pathParams: { bubbleId },
      });
    },
    [bubbleId, dispatchMutation, isBusy],
  );

  const handleActionId = useCallback(
    (actionId: string) => {
      if (!bubbleId) {
        setSnackbar({ message: "Missing school context.", severity: "error" });
        return;
      }
      if (actionId === "custom") {
        setSnackbar({
          message: "Custom emergencies are not available yet. Configure standard actions under Emergency Management.",
          severity: "info",
        });
        return;
      }
      if (actionId === "false-alarm") {
        return;
      }
      if (isDispatchKey(actionId)) {
        dispatchEmergency(actionId);
      }
    },
    [bubbleId, dispatchEmergency],
  );

  const handleFalseAlarm = useCallback(async () => {
    if (!bubbleId || isBusy) {
      return;
    }
    setFalseAlarmLoading(true);
    try {
      const result = await activeDeclarationQuery.refetch();
      if (result.error || !result.data?.id) {
        setSnackbar({ message: "There is no active emergency to dismiss.", severity: "info" });
        return;
      }
      await endDeclarationMutation.mutateAsync({
        body: { endKind: 1 },
        pathParams: { bubbleId, declarationId: result.data.id },
      });
    } finally {
      setFalseAlarmLoading(false);
    }
  }, [activeDeclarationQuery, bubbleId, endDeclarationMutation, isBusy]);

  const tileDisabled = !bubbleId || isBusy;

  return (
    <>
      <QuickResponseTemplate
        falseAlarmDisabled={falseAlarmLoading}
        open={open}
        tileDisabled={tileDisabled}
        onClose={onClose}
        onFalseAlarm={handleFalseAlarm}
        onTilePress={handleActionId}
      />
      <Snackbar
        anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
        autoHideDuration={6000}
        open={snackbar != null}
        onClose={() => setSnackbar(null)}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} sx={{ width: "100%" }} onClose={() => setSnackbar(null)}>
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
}
